import React, { useState, useRef, useEffect } from 'react';

function flagCode(iso) {
  const c = (iso || '').trim().toUpperCase();
  if (c === 'UK') return 'gb';
  return c.length === 2 ? c.toLowerCase() : '';
}

/** Stable key for list selection (API uses numeric id; worldCountries uses ISO code). */
function countryKey(c) {
  if (!c) return '';
  if (c.id != null && c.id !== '') return String(c.id);
  return String(c.code || c.phone_code || '');
}

/** Longest phone_code prefix first (+212 before +1, +994 before +90). */
function detectCountryFromValue(value, countries) {
  if (!value) return null;
  const trimmed = value.replace(/\s/g, '');
  const withCode = countries
    .filter(c => c.phone_code)
    .sort((a, b) => b.phone_code.length - a.phone_code.length);
  for (const c of withCode) {
    if (trimmed.startsWith(c.phone_code)) return c;
  }
  for (const c of countries) {
    if (c.phone_regex && new RegExp(c.phone_regex).test(trimmed)) return c;
  }
  return null;
}

export default function PhoneInput({
  countries = [],
  value = '',
  onChange,
  defaultCountryId,
  error,
  placeholder,
  required = false,
  label,
  name,
}) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [search, setSearch] = useState('');
  const [pickedId, setPickedId] = useState(null);
  const ref = useRef(null);
  const searchRef = useRef(null);

  const dialable = countries.filter(c => c.phone_code);
  const sorted = [...dialable].sort((a, b) => a.phone_code.localeCompare(b.phone_code));

  const detected = detectCountryFromValue(value, countries);
  const defaultCountry = countries.find(
    c => c.id === defaultCountryId
      || c.id === Number(defaultCountryId)
      || String(c.code || '').toUpperCase() === String(defaultCountryId || '').toUpperCase()
  );
  const manualPick = pickedId
    ? countries.find(c => countryKey(c) === String(pickedId))
    : null;
  const selected = (value && detected)
    ? detected
    : manualPick
    || (!value && defaultCountry?.phone_code ? defaultCountry : null)
    || detected
    || manualPick
    || dialable[0]
    || null;

  const phoneCode = selected?.phone_code || '';
  const ph = placeholder || selected?.phone_example || 'رقم الجوال';

  const q = search.trim().toLowerCase();
  const filtered = q
    ? sorted.filter(c =>
        (c.phone_code || '').toLowerCase().includes(q) ||
        (c.name_ar || '').toLowerCase().includes(q) ||
        (c.code || '').toLowerCase().includes(q)
      )
    : sorted;

  function handleSelect(country) {
    if (!country?.phone_code) return;
    setPickedId(countryKey(country));
    setOpen(false);
    setSearch('');
    const newCode = country.phone_code || '';
    const oldCode = phoneCode;
    if (oldCode && value.startsWith(oldCode)) {
      onChange(newCode + value.slice(oldCode.length));
    } else {
      onChange(newCode + value.replace(/^0+/, ''));
    }
  }

  function handleInput(e) {
    const raw = e.target.value;
    const code = phoneCode;
    if (!raw) {
      onChange('');
    } else if (code && raw.startsWith(code)) {
      onChange(raw);
    } else if (code && !raw.startsWith(code) && !raw.startsWith(code.replace('+', ''))) {
      onChange(code + raw.replace(/^0+/, ''));
    } else {
      onChange(raw);
    }
  }

  function openDropdown() {
    setSearch('');
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    if (searchRef.current) searchRef.current.focus();
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const displayNum = value;
  const inputValue = phoneCode && displayNum.startsWith(phoneCode)
    ? displayNum.slice(phoneCode.length)
    : displayNum;

  const borderColor = error ? '#f44336' : focused ? 'var(--color-primary)' : '#ddd';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {label && (
        <label style={{ fontSize: 'var(--font-size-sm)', color: '#555', fontWeight: '600' }}>
          {label}
          {required && <span style={{ color: '#f44336' }}> *</span>}
        </label>
      )}
      <div ref={ref} style={{ position: 'relative' }}>
        <div
          style={{
            display: 'flex', alignItems: 'center', direction: 'ltr',
            border: `1px solid ${borderColor}`,
            borderRadius: '6px', backgroundColor: 'white',
            transition: 'border-color 0.2s',
          }}
        >
          <button
            type="button"
            onClick={() => openDropdown()}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              padding: '0 8px', height: '42px', cursor: 'pointer',
              border: 'none', borderRight: `1px solid ${borderColor}`,
              borderRadius: '6px 0 0 6px',
              backgroundColor: '#fafafa', color: '#333',
              fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap',
              outline: 'none', flexShrink: 0,
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fafafa'}
          >
            <span
              className={`fi fi-${flagCode(selected?.code)}`}
              title={selected?.name_ar || selected?.name || selected?.code || ''}
              style={{ width: '20px', height: '15px', borderRadius: '2px', display: 'block', flexShrink: 0, backgroundSize: 'cover' }}
            />
            <span style={{ direction: 'ltr', unicodeBidi: 'plaintext', color: '#555' }}>
              {phoneCode}
            </span>
            <span style={{ fontSize: '9px', color: '#aaa', marginTop: '1px' }}>▼</span>
          </button>

          <input
            name={name}
            value={inputValue}
            onChange={handleInput}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={ph}
            dir="ltr"
            style={{
              flex: 1, border: 'none', outline: 'none',
              padding: '0 12px', height: '42px',
              fontSize: '15px', color: '#333', boxSizing: 'border-box',
              backgroundColor: 'transparent', minWidth: 0,
            }}
          />
        </div>

        {open && (
          <div dir="ltr" style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0,
            zIndex: 100, backgroundColor: 'white', border: '1px solid #ddd',
            borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            width: 'min(148px, 92vw)', display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <div style={{ padding: '6px', borderBottom: '1px solid #eee' }}>
              <input
                ref={searchRef}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="بحث..."
                dir="ltr"
                style={{
                  width: '100%', padding: '5px 6px', border: '1px solid #ddd',
                  borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box',
                  outline: 'none',
                }}
                onKeyDown={e => {
                  e.stopPropagation();
                  if (e.key === 'Escape') { setOpen(false); setSearch(''); }
                  if (e.key === 'Enter' && filtered.length === 1) {
                    handleSelect(filtered[0]);
                  }
                }}
              />
            </div>
            <div style={{ overflowY: 'auto', maxHeight: '260px' }}>
              {filtered.length === 0 ? (
                <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
                  لا توجد نتائج
                </div>
              ) : (
                filtered.map(c => {
                  const key = countryKey(c);
                  const isSelected = key && key === countryKey(selected);
                  return (
                  <div
                    key={key || c.phone_code}
                    onClick={() => handleSelect(c)}
                    style={{
                      padding: '7px 10px', cursor: 'pointer', display: 'flex',
                      alignItems: 'center', gap: '8px', fontSize: '13px', color: '#333',
                      backgroundColor: isSelected ? '#EDE7F6' : 'transparent',
                      borderBottom: '1px solid #f5f5f5',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = isSelected ? '#EDE7F6' : 'transparent';
                    }}
                  >
                    <span
                      className={`fi fi-${flagCode(c.code)}`}
                      title={c.name_ar || c.name || c.code || ''}
                      style={{ width: '18px', height: '13px', borderRadius: '2px', display: 'block', flexShrink: 0, backgroundSize: 'cover' }}
                    />
                    <span style={{
                      direction: 'ltr', unicodeBidi: 'plaintext',
                      color: '#555', fontWeight: '600',
                    }}>
                      {c.phone_code || ''}
                    </span>
                  </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {error && <span style={{ fontSize: 'var(--font-size-xs)', color: '#f44336', marginTop: '2px' }}>{error}</span>}
    </div>
  );
}
